/**
 * Break-even planning (B-0130). Pure; synced to the API (/v1/business/break-even).
 * Cost-volume-profit identities (any managerial accounting text): contribution per unit = price - variable cost;
 * break-even units = fixed costs / contribution per unit; break-even price at a volume = fixed costs / volume + variable cost;
 * margin of safety = expected sales - break-even sales.
 *
 * Whole units: you cannot sell a fraction, so "units" is rounded UP (334 for 5,000 / 15). The revenue FOR THOSE WHOLE UNITS is
 * 334 x price = 8,350; the exact break-even revenue (no rounding of units) is fixed costs / contribution margin ratio = 8,333.33.
 * Both are returned and labelled so neither is ambiguous. Margin of safety is measured in whole break-even units.
 */
import type { CalcStep } from "@/lib/calc/steps";

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const r1 = (n: number) => Math.round((n + Number.EPSILON) * 10) / 10;
/** ceil that ignores floating-point dust (5000 / 12.5 must be 400, not 401). */
const ceilUnits = (x: number) => Math.ceil(x - 1e-9);

export interface BreakEvenInput {
  fixedCosts: number; price: number; variableCost: number;
  targetProfit?: number;
  /** Units you expect to sell per month. Enables break-even price, margin of safety and monthly profit. */
  expectedUnits?: number;
  /** One-off start-up cost. Needs expectedUnits: months to recover it from the monthly profit. */
  startupCost?: number;
}

export interface BreakEvenPlan {
  contribution: number; contributionPct: number;
  /** Fixed costs / contribution, not rounded. */
  exactUnits: number;
  /** Rounded up to whole units. */
  units: number;
  /** units x price: the revenue when you sell exactly the whole break-even units. */
  revenueAtUnits: number;
  /** exactUnits x price: the break-even revenue without rounding the units. */
  exactRevenue: number;
  target: { profit: number; units: number; revenue: number } | null;
  expected: null | {
    units: number;
    monthlyProfit: number;
    /** Price that covers fixed and variable costs at this volume, rounded UP to the cent so it truly breaks even. */
    breakEvenPrice: number;
    /** Expected - break-even whole units (negative = short of break-even). */
    marginOfSafetyUnits: number;
    marginOfSafetyPct: number;
    startup: null | { cost: number; months: number | null };
  };
}

export type BreakEvenResult = { state: "ok"; plan: BreakEvenPlan } | { state: "bad" } | { state: "loss" };

export function planBreakEven(i: BreakEvenInput): BreakEvenResult {
  const { fixedCosts: fc, price, variableCost: vc } = i;
  if (![fc, price, vc].every(Number.isFinite) || fc < 0 || price < 0 || vc < 0) return { state: "bad" };
  const cm = price - vc;
  if (cm <= 0) return { state: "loss" };
  const exactUnits = fc / cm;
  const units = ceilUnits(exactUnits);
  const tp = i.targetProfit;
  const target = tp !== undefined && Number.isFinite(tp) && tp > 0 ? { profit: tp, units: ceilUnits((fc + tp) / cm), revenue: ceilUnits((fc + tp) / cm) * price } : null;
  let expected: BreakEvenPlan["expected"] = null;
  const eu = i.expectedUnits;
  if (eu !== undefined && Number.isFinite(eu) && eu > 0) {
    const monthlyProfit = eu * cm - fc;
    const su = i.startupCost;
    const startup = su !== undefined && Number.isFinite(su) && su > 0 ? { cost: su, months: monthlyProfit > 0 ? su / monthlyProfit : null } : null;
    expected = {
      units: eu, monthlyProfit,
      breakEvenPrice: Math.ceil((fc / eu + vc) * 100 - 1e-9) / 100,
      marginOfSafetyUnits: eu - units, marginOfSafetyPct: ((eu - units) / eu) * 100,
      startup,
    };
  }
  return { state: "ok", plan: { contribution: cm, contributionPct: (cm / price) * 100, exactUnits, units, revenueAtUnits: units * price, exactRevenue: exactUnits * price, target, expected } };
}

/** The working, from the plan the page shows. `f` formats money (the API passes a plain rounder). */
export function breakEvenSteps(i: BreakEvenInput, p: BreakEvenPlan, f: (n: number) => number | string = r2): CalcStep[] {
  const steps: CalcStep[] = [
    { label: "Contribution per unit", formula: "Price - variable cost per unit", inputs: { price: f(i.price), "variable cost": f(i.variableCost) }, result: f(p.contribution) },
    { label: "Break-even units (exact)", formula: "Fixed costs / contribution per unit", inputs: { "fixed costs": f(i.fixedCosts), contribution: f(p.contribution) }, result: r2(p.exactUnits) },
    { label: "Break-even units (whole units)", formula: "Round the exact figure UP: you cannot sell part of a unit", inputs: { exact: r2(p.exactUnits) }, result: p.units },
    { label: "Revenue for those whole units", formula: "Whole units x price", inputs: { units: p.units, price: f(i.price) }, result: f(p.revenueAtUnits) },
    { label: "Exact break-even revenue", formula: "Fixed costs / contribution margin ratio (= exact units x price)", inputs: { "fixed costs": f(i.fixedCosts), "contribution margin": `${r1(p.contributionPct)}%` }, result: f(p.exactRevenue) },
  ];
  if (p.target) steps.push({ label: "Units for the profit goal", formula: "(Fixed costs + target profit) / contribution per unit, rounded up", inputs: { "fixed costs": f(i.fixedCosts), "target profit": f(p.target.profit), contribution: f(p.contribution) }, result: p.target.units });
  const e = p.expected;
  if (e) {
    steps.push({ label: "Break-even price at your volume", formula: "Fixed costs / expected units + variable cost per unit (rounded up to the cent)", inputs: { "fixed costs": f(i.fixedCosts), "expected units": e.units, "variable cost": f(i.variableCost) }, result: f(e.breakEvenPrice) });
    steps.push({ label: "Monthly profit at your price", formula: "Expected units x contribution per unit - fixed costs", inputs: { "expected units": e.units, contribution: f(p.contribution), "fixed costs": f(i.fixedCosts) }, result: f(e.monthlyProfit) });
    steps.push({ label: "Margin of safety", formula: "(Expected units - break-even whole units) / expected units", inputs: { "expected units": e.units, "break-even units": p.units }, result: `${e.marginOfSafetyUnits} units (${r1(e.marginOfSafetyPct)}%)` });
    if (e.startup) steps.push({ label: "Months to recover start-up cost", formula: "Start-up cost / monthly profit", inputs: { "start-up cost": f(e.startup.cost), "monthly profit": f(e.monthlyProfit) }, result: e.startup.months === null ? "Never at this volume and price" : r1(e.startup.months) });
  }
  return steps;
}

export interface BreakEvenRow { label: string; units: number; revenue: number; totalCost: number; profit: number }
/** Units sold vs revenue, cost and profit around the break-even point (and the expected volume when given). */
export function breakEvenTable(i: BreakEvenInput, p: BreakEvenPlan): BreakEvenRow[] {
  const pts: { label: string; units: number }[] = [
    { label: "Nothing sold", units: 0 },
    { label: "Half of break-even", units: Math.floor(p.units / 2) },
    { label: "Break-even", units: p.units },
    { label: "1.5 x break-even", units: Math.ceil(p.units * 1.5) },
    { label: "2 x break-even", units: p.units * 2 },
  ];
  if (p.expected) pts.push({ label: "Your expected volume", units: p.expected.units });
  const seen = new Set<string>();
  return pts
    .filter((x) => { const k = `${x.label}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .sort((a, b) => a.units - b.units || a.label.localeCompare(b.label))
    .map((x) => ({ label: x.label, units: x.units, revenue: x.units * i.price, totalCost: i.fixedCosts + x.units * i.variableCost, profit: x.units * p.contribution - i.fixedCosts }));
}
